import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { CATEGORY_CONFIG } from '../../utils/categoryConstants';

const CategoryLegend = ({ compact = false, availableProducts = [] }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Use compact mode on mobile or when explicitly requested
  const useCompact = compact || isMobile;

  // Get categories that have available products for this garage
  const getApplicableCategories = () => {
    if (!availableProducts.length) {
      return [];
    }
    
    const categories = new Set();
    availableProducts.forEach(product => {
      if (product.category && CATEGORY_CONFIG[product.category]) {
        categories.add(product.category);
      }
    });
    
    return Array.from(categories);
  };

  const applicableCategories = getApplicableCategories();

  // Don't render if no applicable categories
  if (applicableCategories.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, sm: 2 },
        flexWrap: 'wrap',
        p: { xs: 1, sm: 1.5 },
        bgcolor: 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        mb: 2
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: 'text.secondary',
          fontSize: { xs: '0.7rem', sm: '0.75rem' },
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          flexShrink: 0
        }}
      >
        Categories:
      </Typography>
      
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.5, sm: 1 },
          flexWrap: 'wrap'
        }}
      >
        {applicableCategories.map((category) => {
          const config = CATEGORY_CONFIG[category];
          return (
            useCompact ? (
              // Compact version with just avatar and letter
              <Box
                key={category}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <Avatar
                  sx={{
                    width: { xs: 20, sm: 24 },
                    height: { xs: 20, sm: 24 },
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    fontWeight: 'bold',
                    bgcolor: config.color,
                    color: 'white'
                  }}
                >
                  {config.letter}
                </Avatar>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                    fontWeight: 500,
                    color: 'text.primary'
                  }}
                >
                  {config.name}
                </Typography>
              </Box>
            ) : (
              // Full version with chip
              <Chip
                key={category}
                avatar={
                  <Avatar
                    sx={{
                      bgcolor: config.color + ' !important',
                      color: 'white !important',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {config.letter}
                  </Avatar>
                }
                label={config.name}
                variant="outlined"
                size={isTablet ? "small" : "medium"}
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  fontWeight: 500,
                  color: 'text.primary',
                  borderColor: config.color,
                  bgcolor: config.color + '08', // 8% opacity
                  '&:hover': {
                    bgcolor: config.color + '15' // 15% opacity on hover
                  }
                }}
              />
            )
          );
        })}
      </Box>
    </Box>
  );
};

export default CategoryLegend;